import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import api from "../services/api";
import "./BreakdownPage.css";

const MONTHS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december"
];

const BreakdownPage = () => {
  const { id: businessId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const accountId = queryParams.get("account_id") || null;
  const accountMask = queryParams.get("mask") || null;
  const accountType = queryParams.get("subtype") || null;

  const [breakdown, setBreakdown] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  const [mode, setMode] = useState("plaid"); // 'plaid' | 'cashin'

  const toNum = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  useEffect(() => {
    const current = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => current - i);
    setAvailableYears(years);
  }, []);

  const buildPayrollCostRowFromStats = async () => {
    const start = `${year}-01-01`;
    const end = `${year}-12-31`;
    const res = await api.get(`/payroll/stats/${businessId}`, {
      params: { start_date: start, end_date: end },
    });
    const rows = Array.isArray(res.data) ? res.data : [];

    const sums = {
      january: 0, february: 0, march: 0, april: 0, may: 0, june: 0,
      july: 0, august: 0, september: 0, october: 0, november: 0, december: 0, total: 0,
    };
    for (const r of rows) {
      const d = new Date(r.pay_date);
      if (!Number.isFinite(d.getTime())) continue;
      const mIdx = d.getMonth();
      const key = MONTHS[mIdx];
      const val = toNum(r.payroll_cost);
      sums[key] += val;
      sums.total += val;
    }
    return {
      category: "Payroll Cost",
      type: "expense",
      ...sums,
    };
  };

  const fetchBreakdown = async () => {
    try {
      const url =
        mode === "cashin"
          ? `/reports/cash-in-vs-transactions/${businessId}/breakdown`
          : `/plaid/transactions/${businessId}/breakdown`;
      const params = { year, account_id: accountId };
      const res = await api.get(url, { params });
      let rows = res.data?.data || [];

      if (mode === "cashin") {
        rows = rows.filter(
          (r) =>
            !(r.type === "expense" && /payroll|salary|wages/i.test(String(r.category || "")))
        );
        const payrollRow = await buildPayrollCostRowFromStats();
        rows.push(payrollRow);
      }

      setBreakdown(rows);
    } catch (err) {
      console.error("Failed to fetch breakdown", err);
      alert("Could not load breakdown data.");
    }
  };

  useEffect(() => {
    fetchBreakdown();
    // eslint-disable-next-line
  }, [businessId, year, accountId, mode]);

  const formatMoney = (value) => {
    const num = parseFloat(value || 0);
    return Math.abs(num).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const getColumnTotals = (type) => {
    const totals = {};
    MONTHS.forEach((month) => {
      totals[month] = breakdown
        .filter((row) => row.type === type)
        .reduce((sum, row) => sum + parseFloat(row[month] || 0), 0);
    });
    totals.total = breakdown
      .filter((row) => row.type === type)
      .reduce((sum, row) => sum + parseFloat(row.total || 0), 0);
    return totals;
  };

  const incomeTotals = getColumnTotals("income");
  const expenseTotals = getColumnTotals("expense");

  const formatCategory = (cat) =>
    cat
      ? cat.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : "Uncategorized";

  // ---------- stacked cell renderer ----------
  const renderAmountWithPercent = (amount, percentText, opts = {}) => {
    const num = parseFloat(amount || 0);
    const isZero = num === 0;
    return (
      <div className="bd-cell">
        <div className="bd-amt">
          {isZero ? "" : formatMoney(num)}
        </div>
        <div className="bd-pct">{percentText || ""}</div>
      </div>
    );
  };

  const pctOfIncome = (val, incomeVal) => {
    const v = Math.abs(parseFloat(val || 0));
    const inc = Math.abs(parseFloat(incomeVal || 0));
    if (!inc) return "";
    return ((v / inc) * 100).toFixed(1) + "%";
  };

  const profitMargin = (incomeVal, expenseVal) => {
    const inc = Math.abs(parseFloat(incomeVal || 0));
    if (!inc) return "";
    const prof = Math.abs(inc - Math.abs(parseFloat(expenseVal || 0)));
    return ((prof / inc) * 100).toFixed(1) + "%";
  };

  // 🔥 put back your original PDF download handler
  const handleDownloadPDF = async () => {
    try {
      const endpoint =
        mode === "cashin" ? "/print-breakdown-net" : "/print-breakdown";
      const payload = {
        businessId,
        year,
        account_id: accountId || null,
      };
      const res = await api.post(endpoint, payload, { responseType: "blob" });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        mode === "cashin" ? `breakdown_net_${year}.pdf` : `breakdown_${year}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download PDF:", err);
      alert("Could not download the PDF.");
    }
  };

  return (
    <div className="breakdown-container breakdown-main">
      <h1 className="pw-page-title">Breakdown by Category and Month ({year})</h1>
      <div className="breakdown-block">
        <button
          className="tran-btn"
          type="button"
          onClick={() =>
            navigate(`/dashboard/business/${businessId}/transactions-view`)
          }
        >
          Transactions
        </button>
        <button className="tran-pdf-btn" type="button" onClick={handleDownloadPDF}>
          {mode === "cashin" ? "Save NET PDF" : "Save PDF"}
        </button>
        <button
          className="tran-btn"
          type="button"
          onClick={() => setMode((m) => (m === "cashin" ? "plaid" : "cashin"))}
        >
          {mode === "cashin"
            ? "Back to Transactions Breakdown"
            : "Calculate by Cash-In & Transactions"}
        </button>
        
      </div>
      <div className="breakdown-pill">
      <div className="selected-account">
          {mode === "cashin"
            ? "Source: Income from Cash Register • Expenses from Transactions • Payroll Cost = total employer cost"
            : <>Account: {accountType} {accountMask || "All Accounts"}</>}
        </div>

      <div className="breakdown-year">
        <label><strong>Select Year: </strong></label>{" "}
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {availableYears.map((yr) => (
            <option key={yr} value={yr}>{yr}</option>
          ))}
        </select>
      </div>
      </div>

      <div className=" breakdown-div">
        <table className=" breakdown-table">
          <thead>
            <tr>
              <th className="sticky-col">Category</th>
              {MONTHS.map((m) => (
                <th key={`${m}-header`} className="month-col">{m.toUpperCase()}</th>
              ))}
              <th className="total-col">Total</th>
            </tr>
          </thead>
          <tbody>
            {/* Income categories */}
            {breakdown
              .filter((row) => row.type === "income" && parseFloat(row.total) !== 0)
              .map((row, i) => (
                <tr key={`income-${i}`}>
                  <td className="sticky-col">{formatCategory(row.category)}</td>
                  {MONTHS.map((m) => (
                    <td key={`inc-${i}-${m}`} className="text-end month-col">
                      {renderAmountWithPercent(row[m], "", { negativeIsGreen: true })}
                    </td>
                  ))}
                  <td className="text-end total-col">
                    {renderAmountWithPercent(row.total, "", { bold: true, negativeIsGreen: true })}
                  </td>
                </tr>
              ))}

            {/* Total income */}
            <tr className="total-income">
              <td className="sticky-col"><strong>Total Income</strong></td>
              {MONTHS.map((m) => (
                <td key={`tot-inc-${m}`} className="text-end month-col">
                  {renderAmountWithPercent(incomeTotals[m], "", { bold: true })}
                </td>
              ))}
              <td className="text-end total-col">
                {renderAmountWithPercent(incomeTotals.total, "", { bold: true })}
              </td>
            </tr>

            {/* Expense categories */}
            {breakdown
              .filter((row) => row.type === "expense" && parseFloat(row.total) !== 0)
              .map((row, i) => (
                <tr key={`expense-${i}`}>
                  <td className="sticky-col">{formatCategory(row.category)}</td>
                  {MONTHS.map((m) => (
                    <td key={`exp-${i}-${m}`} className="text-end month-col">
                      {renderAmountWithPercent(
                        row[m],
                        pctOfIncome(row[m], incomeTotals[m]),
                        { negativeIsGreen: true }
                      )}
                    </td>
                  ))}
                  <td className="text-end total-col">
                    {renderAmountWithPercent(
                      row.total,
                      pctOfIncome(row.total, incomeTotals.total),
                      { bold: true, negativeIsGreen: true }
                    )}
                  </td>
                </tr>
              ))}

            {/* Total expense */}
            <tr className="total-expense">
              <td className="sticky-col"><strong>Total Expense</strong></td>
              {MONTHS.map((m) => (
                <td key={`tot-exp-${m}`} className="text-end month-col">
                  {renderAmountWithPercent(
                    expenseTotals[m],
                    pctOfIncome(expenseTotals[m], incomeTotals[m]),
                    { bold: true, negativeIsGreen: true }
                  )}
                </td>
              ))}
              <td className="text-end total-col">
                {renderAmountWithPercent(
                  expenseTotals.total,
                  pctOfIncome(expenseTotals.total, incomeTotals.total),
                  { bold: true, negativeIsGreen: true }
                )}
              </td>
            </tr>

            {/* Net Profit / Loss */}
            <tr className="profit-info">
              <td className="sticky-col"><strong>Net Profit / Loss</strong></td>
              {MONTHS.map((m) => {
                const inc = Math.abs(incomeTotals[m] || 0);
                const exp = Math.abs(expenseTotals[m] || 0);
                const profit = inc - exp;
                return (
                  <td
        key={`profit-${m}`}
        className={`text-end month-col ${profit < 0 ? "negative" : "positive"}`}
      >
                    {renderAmountWithPercent(
                      profit,
                      profitMargin(incomeTotals[m], expenseTotals[m]),
                      { bold: true }
                    )}
                  </td>
                );
              })}
              <td
    className={`text-end total-col ${
      (Math.abs(incomeTotals.total) - Math.abs(expenseTotals.total)) < 0 ? "negative" : "positive"
    }`}
  >
                {renderAmountWithPercent(
                  Math.abs(incomeTotals.total) - Math.abs(expenseTotals.total),
                  profitMargin(incomeTotals.total, expenseTotals.total),
                  { bold: true }
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BreakdownPage;






// import React, { useEffect, useState } from "react";
// import { useParams, useLocation, useNavigate } from "react-router-dom";
// import api from "../services/api";
// import "./BreakdownPage.css";

// const MONTHS = [
//   "jan", "feb", "mar", "apr", "may", "jun",
//   "jul", "aug", "sep", "oct", "nov", "dec"
// ];

// const BreakdownPage = () => {
//   const { id: businessId } = useParams();
//   const location = useLocation();
//   const navigate = useNavigate();

//   const queryParams = new URLSearchParams(location.search);
//   const accountId = queryParams.get("account_id") || null;
//   const accountMask = queryParams.get("mask") || null;
//   const accountType = queryParams.get("subtype") || null;

//   const [breakdown, setBreakdown] = useState([]);
//   const [year, setYear] = useState(new Date().getFullYear());
//   const [availableYears, setAvailableYears] = useState([]);

//   const [mode, setMode] = useState("plaid"); // 'plaid' (current) | 'cashin'


//   // small helper
//   const toNum = (v) => {
//     const n = typeof v === "number" ? v : parseFloat(v);
//     return Number.isFinite(n) ? n : 0;
//   };

//   useEffect(() => {
//     const current = new Date().getFullYear();
//     const years = Array.from({ length: 5 }, (_, i) => current - i);
//     setAvailableYears(years);
//   }, []);




//     // Build a synthesized "Payroll Cost" expense row from /payroll/stats
//   const buildPayrollCostRowFromStats = async () => {
//     const start = `${year}-01-01`;
//     const end = `${year}-12-31`;
//     const res = await api.get(`/payroll/stats/${businessId}`, {
//       params: { start_date: start, end_date: end},
//     });
//     const rows = Array.isArray(res.data) ? res.data : [];

//     // sum by calendar month
//     const sums = {
//       jan: 0, feb: 0, mar: 0, apr: 0, may: 0, jun: 0,
//       jul: 0, aug: 0, sep: 0, oct: 0, nov: 0, dec: 0, total: 0,
//     };
//     for (const r of rows) {
//       const d = new Date(r.pay_date);
//       if (!Number.isFinite(d.getTime())) continue;
//       const mIdx = d.getMonth(); // 0..11
//       const key = MONTHS[mIdx];
//       const val = toNum(r.payroll_cost); // already includes employer taxes
//       sums[key] += val;
//       sums.total += val;
//     }
//     return {
//       category: "Payroll Cost",
//       type: "expense",
//       ...sums,
//     };
//   };

//   const fetchBreakdown = async () => {
//     try {
//        const url =
//      mode === "cashin"
//        ? `/reports/cash-in-vs-transactions/${businessId}/breakdown`
//        : `/plaid/transactions/${businessId}/breakdown`;
//      const params = { year, account_id: accountId }; 
//    const res = await api.get(url, { params });
//       let rows = res.data?.data || [];

//       if (mode === "cashin") {
//         // just in case backend didn’t filter them, drop raw payroll-like expense categories
//         rows = rows.filter(
//           (r) => !(r.type === "expense" && /payroll|salary|wages/i.test(String(r.category || "")))
//         );
//         // append synthesized Payroll Cost expense row from /payroll/stats (includes employer taxes)
//         const payrollRow = await buildPayrollCostRowFromStats();
//         rows.push(payrollRow);
//       }

//       setBreakdown(rows);
//     } catch (err) {
//       console.error("Failed to fetch breakdown", err);
//       alert("Could not load breakdown data.");
//     }
//   };

//   useEffect(() => {
//     fetchBreakdown();
//     // eslint-disable-next-line
//   }, [businessId, year, accountId, mode]);

//   const formatMoney = (value) => {
//     const num = parseFloat(value || 0);
//     return Math.abs(num).toLocaleString("en-US", {
//       style: "currency",
//       currency: "USD",
//     });
//   };

//   const getColumnTotals = (type) => {
//     const totals = {};
//     MONTHS.forEach((month) => {
//       totals[month] = breakdown
//         .filter((row) => row.type === type)
//         .reduce((sum, row) => sum + parseFloat(row[month] || 0), 0);
//     });
//     totals.total = breakdown
//       .filter((row) => row.type === type)
//       .reduce((sum, row) => sum + parseFloat(row.total || 0), 0);
//     return totals;
//   };

//   const incomeTotals = getColumnTotals("income");
//   const expenseTotals = getColumnTotals("expense");

//   const formatCategory = (cat) =>
//     cat
//       ? cat.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
//       : "Uncategorized";

//   // PDF handler
//   const handleDownloadPDF = async () => {
//     try {
//           const endpoint =
//       mode === "cashin" ? "/print-breakdown-net" : "/print-breakdown";
//     const payload = {
//       businessId,
//       year,
//       account_id: accountId || null,
//     };
//     const res = await api.post(endpoint, payload, { responseType: "blob" });

//       const blob = new Blob([res.data], { type: "application/pdf" });
//       const url = window.URL.createObjectURL(blob);

//       const link = document.createElement("a");
//       link.href = url;
//          link.setAttribute(
//      "download",
//      mode === "cashin" ? `breakdown_net_${year}.pdf` : `breakdown_${year}.pdf`
//    );
//       document.body.appendChild(link);
//       link.click();
//       link.remove();
//       window.URL.revokeObjectURL(url);
//     } catch (err) {
//       console.error("Failed to download PDF:", err);
//       alert("Could not download the PDF.");
//     }
//   };

//   return (
//     <div className="breakdown-container breakdown-main">
//       <h5>Breakdown by Category and Month ({year})</h5>
//       <div className="breakdown-block">
//         <button
//           className="tran-btn"
//           type="button"
//           onClick={() => navigate(`/dashboard/business/${businessId}/transactions-view`)}
//         >
//           Transactions
//         </button>
//         <button className="tran-pdf-btn" type="button" onClick={handleDownloadPDF}>
//   {mode === "cashin" ? "Save NET PDF" : "Save PDF"}
// </button>
//          <button
//    className="tran-btn"
//    type="button"
//    onClick={() => setMode(m => (m === "cashin" ? "plaid" : "cashin"))}
//  >
//    {mode === "cashin" ? "Back to Transactions Breakdown" : "Calculate by Cash-In & Transactions"}
//  </button>
//         <div className="selected-account">
             
//         {mode === "cashin"
//      ? "Source: Income from Cash Register • Expenses from Transactions • Payroll Cost = total employer cost"
//      : <>Account: {accountType} {accountMask || "All Accounts"}</>}
//         </div>
//       </div>
//       <div className="breakdown-year">
//         <label><strong>Select Year: </strong></label>{" "}
//         <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
//           {availableYears.map((yr) => (
//             <option key={yr} value={yr}>{yr}</option>
//           ))}
//         </select>
//       </div>

//       <div className="table-responsive breakdown-div">
//         <table className="table table-bordered table-striped breakdown-table">
//           <thead>
//             <tr>
//               <th className="sticky-col">Category</th>
//               {MONTHS.map((m) => (
//                 <>
//                   <th key={`${m}-header`} className=" month-col">{m.toUpperCase()}</th>
//                   <th key={`${m}-percent-header`} className=" percent-col">{m.toUpperCase()}%</th>
//                 </>
//               ))}
//               <th className=" total-col">Total</th>
//               <th className=" percent-col"></th>
//             </tr>
//           </thead>
//           <tbody>
//             {/* Income categories */}
//             {breakdown
//               .filter((row) => row.type === "income" && parseFloat(row.total) !== 0)
//               .map((row, i) => (
//                 <tr key={`income-${i}`}>
//                   <td className="sticky-col">{formatCategory(row.category)}</td>
//                   {MONTHS.map((m) => (
//                     <>
//                       <td key={`${m}-amount`} className="text-end month-col" style={{ color: row[m] < 0 ? "green" : "inherit" }}>
//                         {parseFloat(row[m]) === 0 ? "" : formatMoney(row[m])}
//                       </td>
//                       <td key={`${m}-percent`} className="text-end percent-col"></td>
//                     </>
//                   ))}
//                   <td className="text-end total-col" style={{ fontWeight: "bold", color: row.total < 0 ? "green" : "inherit" }}>
//                     {parseFloat(row.total) === 0 ? "" : formatMoney(row.total)}
//                   </td>
//                   <td className="text-end percent-col"></td>
//                 </tr>
//               ))}

//             {/* Total income */}
//             <tr className="table-success total-income">
//               <td className="sticky-col"><strong>Total Income</strong></td>
//               {MONTHS.map((m) => (
//                 <>
//                   <td key={`${m}-total`} className="text-end month-col">
//                     <strong>{parseFloat(incomeTotals[m]) === 0 ? "" : formatMoney(incomeTotals[m])}</strong>
//                   </td>
//                   <td key={`${m}-percent-total`} className="text-end percent-col"></td>
//                 </>
//               ))}
//               <td className="text-end total-col"><strong>{parseFloat(incomeTotals.total) === 0 ? "" : formatMoney(incomeTotals.total)}</strong></td>
//               <td className="text-end percent-col"></td>
//             </tr>

//             {/* Expense categories */}
//             {breakdown
//               .filter((row) => row.type === "expense" && parseFloat(row.total) !== 0)
//               .map((row, i) => (
//                 <tr key={`expense-${i}`}>
//                   <td className="sticky-col">{formatCategory(row.category)}</td>
//                   {MONTHS.map((m) => (
//                     <>
//                       <td key={`${m}-amount`} className="text-end month-col" style={{ color: row[m] < 0 ? "green" : "inherit" }}>
//                         {parseFloat(row[m]) === 0 ? "" : formatMoney(row[m])}
//                       </td>
//                       <td key={`${m}-percent`} className="text-end percent-col">
//                         {parseFloat(row[m]) !== 0 && parseFloat(incomeTotals[m]) !== 0
//                           ? (Math.abs(parseFloat(row[m]) / parseFloat(incomeTotals[m])) * 100).toFixed(1) + "%"
//                           : ""}
//                       </td>
//                     </>
//                   ))}
//                   <td className="text-end total-col" style={{ fontWeight: "bold", color: row.total < 0 ? "green" : "inherit" }}>
//                     {parseFloat(row.total) === 0 ? "" : formatMoney(row.total)}
//                   </td>
//                   <td className="text-end percent-col">
//                     {parseFloat(row.total) !== 0 && parseFloat(incomeTotals.total) !== 0
//                       ? (Math.abs(parseFloat(row.total) / parseFloat(incomeTotals.total)) * 100).toFixed(1) + "%"
//                       : ""}
//                   </td>
//                 </tr>
//               ))}

//             {/* Total expense */}
//             <tr className="table-danger total-expense">
//               <td className="sticky-col"><strong>Total Expense</strong></td>
//               {MONTHS.map((m) => (
//                 <>
//                   <td key={`${m}-total`} className="text-end month-col" style={{ color: expenseTotals[m] < 0 ? "green" : "inherit" }}>
//                     <strong>{parseFloat(expenseTotals[m]) === 0 ? "" : formatMoney(expenseTotals[m])}</strong>
//                   </td>
//                   <td key={`${m}-percent-total`} className="text-end percent-col">
//                     {parseFloat(expenseTotals[m]) !== 0 && parseFloat(incomeTotals[m]) !== 0
//                       ? (Math.abs(parseFloat(expenseTotals[m]) / parseFloat(incomeTotals[m])) * 100).toFixed(1) + "%"
//                       : ""}
//                   </td>
//                 </>
//               ))}
//               <td className="text-end total-col" style={{ fontWeight: "bold", color: expenseTotals.total < 0 ? "green" : "inherit" }}>
//                 <strong>{parseFloat(expenseTotals.total) === 0 ? "" : formatMoney(expenseTotals.total)}</strong>
//               </td>
//               <td className="text-end percent-col">
//                 {parseFloat(expenseTotals.total) !== 0 && parseFloat(incomeTotals.total) !== 0
//                   ? (Math.abs(parseFloat(expenseTotals.total) / parseFloat(incomeTotals.total)) * 100).toFixed(1) + "%"
//                   : ""}
//               </td>
//             </tr>

//             {/* Net Profit / Loss */}
//             <tr className="table-info profit-info">
//               <td className="sticky-col"><strong>Net Profit / Loss</strong></td>
//               {MONTHS.map((m) => {
//                 const income = Math.abs(incomeTotals[m] || 0);
//                 const expense = Math.abs(expenseTotals[m] || 0);
//                 const profit = income - expense;
//                 return (
//                   <>
//                     <td key={`${m}-profit`} className="text-end month-col" style={{ color: profit < 0 ? "red" : "green" }}>
//                       <strong>{profit === 0 ? "" : formatMoney(profit)}</strong>
//                     </td>
//                     <td key={`${m}-percent-profit`} className="text-end percent-col">
//                       {income !== 0
//                         ? (Math.abs((profit / income) * 100)).toFixed(1) + "%"
//                         : ""}
//                     </td>
//                   </>
//                 );
//               })}
//               <td className="text-end month-col" style={{
//                 fontWeight: "bold",
//                 color: (Math.abs(incomeTotals.total) - Math.abs(expenseTotals.total)) < 0 ? "red" : "green"
//               }}>
//                 <strong>
//                   {(Math.abs(incomeTotals.total) - Math.abs(expenseTotals.total)) === 0
//                     ? ""
//                     : formatMoney(Math.abs(incomeTotals.total) - Math.abs(expenseTotals.total))}
//                 </strong>
//               </td>
//               <td className="text-end percent-col">
//                 {incomeTotals.total !== 0
//                   ? (Math.abs(((incomeTotals.total - expenseTotals.total) / incomeTotals.total)) * 100).toFixed(1) + "%"
//                   : ""}
//               </td>
//             </tr>
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default BreakdownPage;



// import React, { useEffect, useState } from "react";
// import { useParams, useLocation, useNavigate } from "react-router-dom";
// import api from "../services/api";
// import "./BreakdownPage.css";


// const MONTHS = [
//   "jan", "feb", "mar", "apr", "may", "jun",
//   "jul", "aug", "sep", "oct", "nov", "dec"
// ];

// const BreakdownPage = () => {
//   const { id: businessId } = useParams();
//   const location = useLocation();
//   const navigate = useNavigate();

//   const queryParams = new URLSearchParams(location.search);
//   const accountId = queryParams.get("account_id") || null;
//   const accountMask = queryParams.get("mask") || null;
//   const accountType = queryParams.get("subtype") || null;

//   const [breakdown, setBreakdown] = useState([]);
//   const [year, setYear] = useState(new Date().getFullYear());
//   const [availableYears, setAvailableYears] = useState([]);
 

//   useEffect(() => {
//     const current = new Date().getFullYear();
//     const years = Array.from({ length: 5 }, (_, i) => current - i);
//     setAvailableYears(years);
//   }, []);

//   const fetchBreakdown = async () => {
//     try {
//       const res = await api.get(`/api/plaid/transactions/${businessId}/breakdown`, {
//         params: { year, account_id: accountId }
//       });
//       setBreakdown(res.data.data || []);
//     } catch (err) {
//       console.error("Failed to fetch breakdown", err);
//       alert("Could not load breakdown data.");
//     }
//   };

//   useEffect(() => {
//     fetchBreakdown();
//     // eslint-disable-next-line
//   }, [businessId, year, accountId]);

//   const formatMoney = (value) => {
//     const num = parseFloat(value || 0);
//     return Math.abs(num).toLocaleString("en-US", {
//       style: "currency",
//       currency: "USD",
//     });
//   };

//   const getColumnTotals = (type) => {
//     const totals = {};
//     MONTHS.forEach((month) => {
//       totals[month] = breakdown
//         .filter((row) => row.type === type)
//         .reduce((sum, row) => sum + parseFloat(row[month] || 0), 0);
//     });
//     totals.total = breakdown
//       .filter((row) => row.type === type)
//       .reduce((sum, row) => sum + parseFloat(row.total || 0), 0);
//     return totals;
//   };

//   const incomeTotals = getColumnTotals("income");
//   const expenseTotals = getColumnTotals("expense");

//   const formatCategory = (cat) =>
//     cat
//       ? cat.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
//       : "Uncategorized";

//   return (
//     <div className="breakdown-container breakdown-main">
//       <h5>Breakdown by Category and Month ({year})</h5>
//       <div className="breakdown-block">
//       <button
//                         className="tran-btn"
//                         type="button"
//                         onClick={() => navigate(`/dashboard/business/${businessId}/transactions-view`)}
//                     >
//                         Transactions
//                     </button>
//                     <div className="selected-account">Account: {accountType} {accountMask || "All Accounts"} </div>
//                     </div>
//       <div className="breakdown-year">
//         <label><strong>Select Year: </strong></label>{" "}
//         <select value={year} onChange={(e) => setYear(e.target.value)}>
//           {availableYears.map((yr) => (
//             <option key={yr} value={yr}>{yr}</option>
//           ))}
//         </select>        
//       </div>
    

//       <div className="table-responsive breakdown-div">
//         <table className="table table-bordered table-striped breakdown-table">
//           <thead>
//             <tr>
//               <th className="sticky-col">Category</th>
//               {MONTHS.map((m) => (
//                 <th key={m} className="text-end month-col">{m.toUpperCase()}</th>
//               ))}
//               <th className="text-end total-col">Total</th>
//             </tr>
//           </thead>
//           <tbody>
//             {/* Income categories */}
//             {breakdown
//               .filter((row) => row.type === "income" && parseFloat(row.total) !== 0)
//               .map((row, i) => (
//                 <tr key={`income-${i}`}>
//                   <td className="sticky-col">{formatCategory(row.category)}</td>
//                   {MONTHS.map((m) => (
//                     <td key={m} className="text-end month-col" style={{ color: row[m] < 0 ? "green" : "inherit" }}>
//                       {parseFloat(row[m]) === 0 ? "" : formatMoney(row[m])}
//                     </td>
//                   ))}
//                   <td className="text-end total-col" style={{ fontWeight: "bold", color: row.total < 0 ? "green" : "inherit" }}>
//                     {parseFloat(row.total) === 0 ? "" : formatMoney(row.total)}
//                   </td>
//                 </tr>
//               ))}

//             {/* Total income */}
//             <tr className="table-success total-income">
//               <td className="sticky-col"><strong>Total Income</strong></td>
//               {MONTHS.map((m) => (
//                 <td key={m} className="text-end month-col">
//                   <strong>{parseFloat(incomeTotals[m]) === 0 ? "" : formatMoney(incomeTotals[m])}</strong>
//                 </td>
//               ))}
//               <td className="text-end total-col"><strong>{parseFloat(incomeTotals.total) === 0 ? "" : formatMoney(incomeTotals.total)}</strong></td>
//             </tr>

//             {/* Expense categories */}
//             {breakdown
//               .filter((row) => row.type === "expense" && parseFloat(row.total) !== 0)
//               .map((row, i) => (
//                 <tr key={`expense-${i}`}>
//                   <td className="sticky-col">{formatCategory(row.category)}</td>
//                   {MONTHS.map((m) => (
//                     <td key={m} className="text-end month-col" style={{ color: row[m] < 0 ? "green" : "inherit" }}>
//                       {parseFloat(row[m]) === 0 ? "" : formatMoney(row[m])}
//                     </td>
//                   ))}
//                   <td className="text-end total-col" style={{ fontWeight: "bold", color: row.total < 0 ? "green" : "inherit" }}>
//                     {parseFloat(row.total) === 0 ? "" : formatMoney(row.total)}
//                   </td>
//                 </tr>
//               ))}

//             {/* Total expense */}
//             <tr className="table-danger total-expense">
//               <td className="sticky-col"><strong>Total Expense</strong></td>
//               {MONTHS.map((m) => (
//                 <td key={m} className="text-end month-col" style={{ color: expenseTotals[m] < 0 ? "green" : "inherit" }}>
//                   <strong>{parseFloat(expenseTotals[m]) === 0 ? "" : formatMoney(expenseTotals[m])}</strong>
//                 </td>
//               ))}
//               <td className="text-end total-col" style={{ fontWeight: "bold", color: expenseTotals.total < 0 ? "green" : "inherit" }}>
//                 <strong>{parseFloat(expenseTotals.total) === 0 ? "" : formatMoney(expenseTotals.total)}</strong>
//               </td>
//             </tr>
//             {/* Net Profit / Loss */}
// <tr className="table-info profit-info">
//   <td className="sticky-col"><strong>Net Profit / Loss</strong></td>
//   {MONTHS.map((m) => {
//     const income = Math.abs(incomeTotals[m] || 0);
//     const expense = Math.abs(expenseTotals[m] || 0);
//     const profit = income - expense;
//     return (
//       <td key={m} className="text-end month-col" style={{ color: profit < 0 ? "red" : "green" }}>
//         <strong>{profit === 0 ? "" : formatMoney(profit)}</strong>
//       </td>
//     );
//   })}
//   <td className="text-end month-col" style={{
//     fontWeight: "bold",
//     color: (Math.abs(incomeTotals.total) - Math.abs(expenseTotals.total)) < 0 ? "red" : "green"
//   }}>
//     <strong>
//       {(Math.abs(incomeTotals.total) - Math.abs(expenseTotals.total)) === 0
//         ? ""
//         : formatMoney(Math.abs(incomeTotals.total) - Math.abs(expenseTotals.total))}
//     </strong>
//   </td>
// </tr>

//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default BreakdownPage;



// import React, { useEffect, useState } from "react";
// import { useParams, useLocation } from "react-router-dom";
// import api from "../services/api";

// const MONTHS = [
//   "jan", "feb", "mar", "apr", "may", "jun",
//   "jul", "aug", "sep", "oct", "nov", "dec"
// ];

// const BreakdownPage = () => {
//   const { id: businessId } = useParams();
//   const location = useLocation();

//   const queryParams = new URLSearchParams(location.search);
//   const accountId = queryParams.get("account_id") || null;

//   const [breakdown, setBreakdown] = useState([]);
//   const [year, setYear] = useState(new Date().getFullYear());
//   const [availableYears, setAvailableYears] = useState([]);

//   useEffect(() => {
//     const current = new Date().getFullYear();
//     const years = Array.from({ length: 5 }, (_, i) => current - i);
//     setAvailableYears(years);
//   }, []);

//   const fetchBreakdown = async () => {
//     try {
//       const res = await api.get(`/api/plaid/transactions/${businessId}/breakdown`, {
//         params: { year, account_id: accountId }
//       });
//       setBreakdown(res.data.data || []);
//     } catch (err) {
//       console.error("❌ Failed to fetch breakdown", err);
//       alert("Could not load breakdown data.");
//     }
//   };

//   useEffect(() => {
//     fetchBreakdown();
//     // eslint-disable-next-line
//   }, [businessId, year, accountId]);

//   const formatMoney = (value) => {
//     const num = parseFloat(value || 0);
//     return Math.abs(num).toLocaleString("en-US", {
//       style: "currency",
//       currency: "USD",
//     });
//   };

//   const getColumnTotals = (type) => {
//     const totals = {};
//     MONTHS.forEach((month) => {
//       totals[month] = breakdown
//         .filter((row) => row.type === type)
//         .reduce((sum, row) => sum + parseFloat(row[month] || 0), 0);
//     });
//     totals.total = breakdown
//       .filter((row) => row.type === type)
//       .reduce((sum, row) => sum + parseFloat(row.total || 0), 0);
//     return totals;
//   };

//   const incomeTotals = getColumnTotals("income");
//   const expenseTotals = getColumnTotals("expense");

//   const formatCategory = (cat) =>
//     cat
//       ? cat.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
//       : "Uncategorized";

//   return (
//     <div className="container mt-4">
//       <h4>Breakdown by Category and Month ({year})</h4>

//       <div style={{ marginBottom: "20px" }}>
//         <label><strong>Select Year: </strong></label>{" "}
//         <select value={year} onChange={(e) => setYear(e.target.value)}>
//           {availableYears.map((yr) => (
//             <option key={yr} value={yr}>{yr}</option>
//           ))}
//         </select>
//       </div>

//       <div className="table-responsive">
//         <table className="table table-bordered table-striped">
//           <thead>
//             <tr>
//               <th>Category</th>
//               {MONTHS.map((m) => (
//                 <th key={m} className="text-end">{m.toUpperCase()}</th>
//               ))}
//               <th className="text-end">Total</th>
//             </tr>
//           </thead>
//           <tbody>
//             {breakdown.map((row, i) => (
//               <tr key={i}>
//                 <td>{formatCategory(row.category)}</td>
//                 {MONTHS.map((m) => (
//                   <td key={m} className="text-end" style={{ color: row[m] < 0 ? "green" : "inherit" }}>
//                     {formatMoney(row[m])}
//                   </td>
//                 ))}
//                 <td className="text-end" style={{ fontWeight: "bold", color: row.total < 0 ? "green" : "inherit" }}>
//                   {formatMoney(row.total)}
//                 </td>
//               </tr>
//             ))}
//             <tr className="table-success">
//               <td><strong>Total Income</strong></td>
//               {MONTHS.map((m) => (
//                 <td key={m} className="text-end"><strong>{formatMoney(incomeTotals[m])}</strong></td>
//               ))}
//               <td className="text-end"><strong>{formatMoney(incomeTotals.total)}</strong></td>
//             </tr>
//             <tr className="table-danger">
//               <td><strong>Total Expense</strong></td>
//               {MONTHS.map((m) => (
//                 <td key={m} className="text-end" style={{ color: expenseTotals[m] < 0 ? "green" : "inherit" }}>
//                   <strong>{formatMoney(expenseTotals[m])}</strong>
//                 </td>
//               ))}
//               <td className="text-end" style={{ fontWeight: "bold", color: expenseTotals.total < 0 ? "green" : "inherit" }}>
//                 <strong>{formatMoney(expenseTotals.total)}</strong>
//               </td>
//             </tr>
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default BreakdownPage;



// import React, { useEffect, useState } from "react";
// import { useParams, useLocation } from "react-router-dom";
// import api from "../services/api";

// const MONTHS = [
//   "jan", "feb", "mar", "apr", "may", "jun",
//   "jul", "aug", "sep", "oct", "nov", "dec"
// ];

// const BreakdownPage = () => {
//   const { id: businessId } = useParams();
//   const location = useLocation();

//   const queryParams = new URLSearchParams(location.search);
//   const accountId = queryParams.get("account_id") || null;

//   const [breakdown, setBreakdown] = useState([]);
//   const [year, setYear] = useState(new Date().getFullYear());
//   const [availableYears, setAvailableYears] = useState([]);

//   useEffect(() => {
//     const current = new Date().getFullYear();
//     const years = Array.from({ length: 5 }, (_, i) => current - i);
//     setAvailableYears(years);
//   }, []);

//   const fetchBreakdown = async () => {
//     try {
//       const res = await api.get(`/api/plaid/transactions/${businessId}/breakdown`, {
//         params: { year, account_id: accountId }
//       });
//       setBreakdown(res.data.data || []);
//     } catch (err) {
//       console.error("Failed to fetch breakdown", err);
//       alert("Could not load breakdown data.");
//     }
//   };

//   useEffect(() => {
//     fetchBreakdown();
//     // eslint-disable-next-line
//   }, [businessId, year, accountId]);

//   const formatMoney = (value) => {
//     const num = parseFloat(value || 0);
//     return Math.abs(num).toLocaleString("en-US", {
//       style: "currency",
//       currency: "USD",
//     });
//   };

//   const getColumnTotals = (type) => {
//     const totals = {};
//     MONTHS.forEach((month) => {
//       totals[month] = breakdown
//         .filter((row) => row.type === type)
//         .reduce((sum, row) => sum + parseFloat(row[month] || 0), 0);
//     });
//     totals.total = breakdown
//       .filter((row) => row.type === type)
//       .reduce((sum, row) => sum + parseFloat(row.total || 0), 0);
//     return totals;
//   };

//   const incomeTotals = getColumnTotals("income");
//   const expenseTotals = getColumnTotals("expense");

//   const formatCategory = (cat) =>
//     cat
//       ? cat.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
//       : "Uncategorized";

//   return (
//     <div className="container mt-4">
//       <h4>Breakdown by Category and Month ({year})</h4>

//       <div style={{ marginBottom: "20px" }}>
//         <label><strong>Select Year: </strong></label>{" "}
//         <select value={year} onChange={(e) => setYear(e.target.value)}>
//           {availableYears.map((yr) => (
//             <option key={yr} value={yr}>
//               {yr}
//             </option>
//           ))}
//         </select>
//       </div>

//       <div className="table-responsive">
//         <table className="table table-bordered table-striped">
//           <thead>
//             <tr>
//               <th>Category</th>
//               {MONTHS.map((m) => (
//                 <th key={m} className="text-end">{m.toUpperCase()}</th>
//               ))}
//               <th className="text-end">Total</th>
//             </tr>
//           </thead>
//           <tbody>
//             {breakdown.map((row, i) => (
//               <tr key={i}>
//                 <td>{formatCategory(row.category)}</td>
//                 {MONTHS.map((m) => (
//                   <td key={m} className="text-end" style={{ color: row[m] < 0 ? "green" : "inherit" }}>
//                     {formatMoney(row[m])}
//                   </td>
//                 ))}
//                 <td className="text-end" style={{ fontWeight: "bold", color: row.total < 0 ? "green" : "inherit" }}>
//                   {formatMoney(row.total)}
//                 </td>
//               </tr>
//             ))}
//             <tr className="table-success">
//               <td><strong>Total Income</strong></td>
//               {MONTHS.map((m) => (
//                 <td key={m} className="text-end">
//                   <strong>{formatMoney(incomeTotals[m])}</strong>
//                 </td>
//               ))}
//               <td className="text-end"><strong>{formatMoney(incomeTotals.total)}</strong></td>
//             </tr>
//             <tr className="table-danger">
//               <td><strong>Total Expense</strong></td>
//               {MONTHS.map((m) => (
//                 <td key={m} className="text-end" style={{ color: expenseTotals[m] < 0 ? "green" : "inherit" }}>
//                   <strong>{formatMoney(expenseTotals[m])}</strong>
//                 </td>
//               ))}
//               <td className="text-end" style={{ fontWeight: "bold", color: expenseTotals.total < 0 ? "green" : "inherit" }}>
//                 <strong>{formatMoney(expenseTotals.total)}</strong>
//               </td>
//             </tr>
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default BreakdownPage;
